/**
 * Source-review fact correction, rejection, and task assignment.
 */
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";

export function bindTaskAssignmentActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLFormElement>("[data-task-assign-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const taskId = form.dataset.taskId;
      const assignedTo = form.querySelector<HTMLInputElement>("[data-task-assignee-input]")?.value ?? "";
      if (taskId) void submitTaskAssign(taskId, assignedTo, statusElement, actions);
    });
  });
}

export function bindFactCorrectionActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLFormElement>("[data-fact-correction-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const factId = form.dataset.factId;
      if (!factId) return;
      const fieldName = form.dataset.factField || "fact";
      const value = form.querySelector<HTMLInputElement>("[data-fact-correction-value]")?.value ?? "";
      const reason = form.querySelector<HTMLInputElement>("[data-fact-correction-reason]")?.value ?? "";
      void submitFactCorrection(
        factId,
        fieldName,
        value,
        reason,
        statusElement,
        actions
      );
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-fact-reject]").forEach((button) => {
    button.addEventListener("click", () => {
      const factId = button.dataset.factId;
      if (!factId) return;
      const fieldName = button.dataset.factField || "fact";
      void submitFactRejection(factId, fieldName, statusElement, actions);
    });
  });
}

export async function submitFactCorrection(
  factId: string,
  fieldName: string,
  valueValue: string,
  reasonValue: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  const value = valueValue.trim();
  if (!value) {
    statusElement.textContent = "Fact correction cancelled; no value entered.";
    return;
  }
  const reason = reasonValue.trim() || "operator_review";
  statusElement.textContent = `${factId}: correcting fact...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/facts/${encodeURIComponent(factId)}/correct`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        value,
        normalizedValue: value,
        reason,
        note: `Dashboard corrected ${fieldName}.`
      })
    });
    const body = (await response.json()) as { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${factId}: fact corrected. Reopen detail to refresh.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Fact correction failed.";
  }
}

export async function submitFactRejection(factId: string, fieldName: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  statusElement.textContent = `${factId}: rejecting fact...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/facts/${encodeURIComponent(factId)}/reject`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        reason: "operator_rejected_bad_fact",
        note: `Dashboard rejected ${fieldName}.`
      })
    });
    const body = (await response.json()) as { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${factId}: fact rejected. Reopen detail to refresh.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Fact rejection failed.";
  }
}

export async function submitTaskAssign(taskId: string, assignedToValue: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const assignedTo = assignedToValue.trim();
  if (!assignedTo) {
    statusElement.textContent = "Task assignment cancelled; no assignee entered.";
    return;
  }
  statusElement.textContent = `${taskId}: assigning task...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/tasks/${encodeURIComponent(taskId)}/assign`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        assignedTo,
        note: "Assigned from Lead Factory dashboard."
      })
    });
    const body = (await response.json()) as { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${taskId}: assigned to ${assignedTo}. Reopen detail to refresh.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Task assignment failed.";
  }
}

